<template>
  <MediaLibraryDetailLayout
    :media-library="mediaLibrary"
    :loading="loading"
  >
    <template #leftHeader>
      <StoreInfo
        v-if="store"
        :name="store.attributes.name"
        :image-url="store.attributes.brand_image_url"
      />
    </template>
    <template #rightHeader>
      <button class="backButton c-button__item c-button__item--outline" @click="onBack">
        {{ $t('labels.backToPreviousPage') }}
      </button>
    </template>

    <template #extra>
      <div v-if="mediaLibrary" class="p-table">
        <div class="p-table__content">
          <table class="libraryTable">
            <thead>
              <tr>
                <th><span>{{ $t('labels.category') }}</span></th>
                <th><span>{{ $t('pages.CompaniesLocationStoreMediaLibrary.usingService') }}</span></th>
                <th><span>{{ $t('labels.status') }}</span></th>
                <th v-if="showMoreThanEditorAuthority"><span>{{ $t('labels.operation') }}</span></th>
              </tr>
              <tr class="p-table__content__space"><td colspan="4" /></tr>
            </thead>
            <tbody>
              <tr>
                <td :rowspan="mediaLibrary.services.length">{{ categoryName(mediaLibrary.gmbCategory) }}</td>
                <td>{{ serviceName(mediaLibrary.services[0].name) }}</td>
                <td class="status">
                  <span :class="`status__circle--${ mediaLibrary.services[0].status }`" />
                  {{ $t(`pages.CompaniesMediaLibrary.status.${mediaLibrary.services[0].status}` ) }}
                </td>
                <td v-if="showMoreThanEditorAuthority">
                  <button type="button" class="removeButton" @click="onDeleteMediaLibraryStore(mediaLibrary.services[0].name)">
                    <Icon name="iconCross" class="removeButton__icon" />
                    {{ $t('pages.CompaniesLocationStoreMediaLibrary.removeButton') }}
                  </button>
                </td>
              </tr>
              <template v-if="mediaLibrary.services.length > 1">
                <template v-for="(status, i) in mediaLibrary.services" :key="i">
                  <tr v-if="i > 0">
                    <td>{{ serviceName(status.name) }}</td>
                    <td class="status">
                      <span :class="`status__circle--${ status.status }`" />
                      {{ $t(`pages.CompaniesMediaLibrary.status.${status.status}` ) }}
                    </td>
                    <td v-if="showMoreThanEditorAuthority">
                      <button type="button" class="removeButton" @click="onDeleteMediaLibraryStore(status.name)">
                        <Icon name="iconCross" class="removeButton__icon" />
                        {{ $t('pages.CompaniesLocationStoreMediaLibrary.removeButton') }}
                      </button>
                    </td>
                  </tr>
                </template>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </MediaLibraryDetailLayout>
</template>

<script>
import {mapState, mapActions, mapGetters} from 'vuex'
import MediaLibraryDetailLayout from '../components/CompaniesMediaLibraries/MediaLibraryDetailLayout.vue'
import StoreInfo from '../components/CompaniesMediaLibraries/StoreInfo.vue'
import Icon from '../components/Icon.vue'
import { mediaLibraryCategoriesConst } from '../modules/mediaLibraryCategoriesConst'

export default {
  name: 'CompaniesLocationStoreMediaLibrary',
  components: {
    MediaLibraryDetailLayout,
    StoreInfo,
    Icon,
  },
  emits: [
    'update-title'
  ],
  data () {
    return {
      title: ''
    }
  },
  computed: {
    ...mapState({
      store: state => state.companiesLocationStore.data,
      mediaLibrary: state => state.companiesStoreMediaLibrary.data,
      loading: state => state.companiesStoreMediaLibrary.loading || state.companiesStoreMediaLibraryStore.loading,
    }),
    ...mapGetters({
      showMoreThanEditorAuthority: 'companiesAccessibleApi/showMoreThanEditorAuthority',
    }),
    companyUuid() {
      return this.$route.params.company_uuid
    },
    storeUuid() {
      return this.$route.params.store_uuid
    },
    mediaLibraryId() {
      return this.$route.params.id
    },
  },
  watch: {
    title(newTitle) { this.$emit('update-title', newTitle) }
  },
  async created() {
    this.readMediaLibrary({ storeUuid: this.storeUuid, mediaLibraryId: this.mediaLibraryId })
    await this.readStore({ company_uuid: this.companyUuid, store_uuid: this.storeUuid })
    this.title = this.$t('pages.CompaniesLocationStoreMediaLibrary.title', { name: this.store.attributes.name })
  },
  methods: {
    ...mapActions({
      readStore: 'companiesLocationStore/readStore',
      readMediaLibrary: 'companiesStoreMediaLibrary/readMediaLibrary',
      deleteMediaLibraryStore: 'companiesStoreMediaLibraryStore/deleteMediaLibraryStore',
    }),
    categoryName(gmbCategory) {
      return mediaLibraryCategoriesConst.find(categoryConst => categoryConst.id === gmbCategory)?.name
    },
    serviceName(service) {
      switch (service) {
      case 'google_map':
        return this.$t('labels.sites.google')
      case 'yahoo_map':
        return this.$t('labels.sites.yahooMap')
      default:
        return this.$t(`labels.sites.${service}`)
      }
    },
    async onDeleteMediaLibraryStore(service) {
      if (!confirm(this.$t('pages.CompaniesLocationStoreMediaLibrary.confirmToDeleteMediaLibraryStore', { service: this.serviceName(service) }))) return

      const isSuccess = await this.deleteMediaLibraryStore({ storeUuid: this.storeUuid, mediaLibraryId: this.mediaLibraryId, service })
      if (!isSuccess) return

      this.onBack()
    },
    onBack() {
      this.$router.go(-1)
    }
  },
}
</script>

<style lang="scss" scoped>
@import "../../../stylesheets/biz_spa/foundation/_var.scss";
.uploadError {
  margin-bottom: 12px;
  color: $color-accent-primary;
  font-weight: bold;
}
.backButton {
  font-weight: normal;
  text-decoration: none;
}
.removeButton {
  display: flex;
  align-items: center;
  gap: 0 10px;
  line-height: 1;
  &__icon {
    color: #3A3B3F;
  }
}
.status {
  %__circle {
    display: inline-block;
    width: 10px;
    height: 10px;
    margin-right: 5px;
    border-radius: 100%;
  }
  &__circle {
    &--complete {
      @extend %__circle;
      background: $complete-color;
    }
    &--error {
      @extend %__circle;
      background: $color-accent-primary;
    }
    &--progress {
      @extend %__circle;
      background: #D2D5DB;
    }
  }
  .error {
    color: $color-accent-primary;
    text-decoration: underline;
    cursor: pointer;
  }
}
.libraryTable {
  tbody {
    tr {
      &:first-child {
        td:first-child {
          border-bottom: 1px solid $color-base-c;
          border-bottom-left-radius: 4px;
        }
      }
      &:last-child {
        td {
          border-bottom-left-radius: 0;
        }
      }
    }
  }
}
</style>
